// 入力フォームの初期値 (initialValues属性にセットする)
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useClient } from 'urql';
import { useToast } from 'views/components/atoms/toast/hook';
import { CustomerPagePath } from 'views/constants/enums/pageTitle';
import { GetNegotiateTokenDocument, useLoginMutation } from 'views/utils/graphql/graphql';
import {
  useEstablishStore,
  useNowEstablishPageTypeStore,
  useIsPrevPageConfirmStore,
} from '../accountEstablishment/hooks';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { OpenAccountParamKey } from 'views/constants/const/AccountOpen';
import {
  SignGateStateKey,
  SignGateCodeVerifierKey,
  SignGateAccountKey,
  SignGateRequestBody,
  AuthorizedRequestParams,
  generateRandomNumericString,
  generatePkceCodeVerifier,
  generatePkceCodeChallenge,
  SignGateResponse,
  isSignGateSuccess,
  fetchDataWithAuthorization,
} from 'views/constants/const/SignGate';
import { localStorage } from 'views/utils/localStrage/localStrageUtil';
import { fetchData } from 'views/utils/fetchData';

type LoginParamType = {
  branch: string;
  account: string;
  password: string;
};

type LoginLocationState = {
  errorMessage?: string;
};

export const useLogin = () => {
  const [loginlData, login] = useLoginMutation();
  const [isRevealPassword, setRevealPassword] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toastProps, showToast } = useToast();
  const { clear } = useEstablishStore();
  const { reset } = useNowEstablishPageTypeStore();
  const { isPrevPageConfirm, setIsPrevPageConfirm } = useIsPrevPageConfirmStore();
  const client = useClient();

  useEffect(() => {
    setIsPrevPageConfirm(false);
  }, []);

  useEffect(() => {
    clear();
    reset();
  }, [clear, reset]);

  useEffect(() => {
    const errorMessage = (location.state as LoginLocationState | null)?.errorMessage;
    if (!errorMessage) {
      return;
    }

    showToast(errorMessage, ToastMessageLevel.Error);
    navigate(CustomerPagePath.Login, { replace: true, state: null });
  }, [location.state, navigate, showToast]);

  const validate = (values: LoginParamType) => {
    const errors: Partial<{
      branch: string;
      account: string;
      password: string;
    }> = {};

    if (!values.branch) {
      errors.branch = '口座番号(3桁)が未入力です';
    }
    if (!values.account) {
      errors.account = '口座番号(7桁)が未入力です';
    }
    if (!values.password) {
      errors.password = 'パスワードが未入力です';
    }
    return errors;
  };

  const togglePassword = () => {
    setRevealPassword(!isRevealPassword);
  };

  const onSubmit = useOnceSubmit<LoginParamType>(
    {
      onSubmit: async (values) => {
        const ret = await login({
          input: {
            Account: `${values.branch}-${values.account}`,
            Password: values.password,
          },
        });

        if (ret.data && ret.data?.Login?.Code !== 0) {
          showToast(ret.data?.Login?.Message ?? 'ログインに失敗しました', ToastMessageLevel.Error);
        } else if (ret.data?.Login?.Code === 0) {
          sessionStorage.setItem('last_4_digits', ret.data.Login.PhoneNumber!);
          // パスキーログイン失敗してパスワードログインした場合、セッションに検証用の値が残っているので削除
          sessionStorage.removeItem(SignGateStateKey);
          sessionStorage.removeItem(SignGateCodeVerifierKey);
          sessionStorage.removeItem(SignGateAccountKey);
          // ログイン時に口座開設パラメータの削除
          localStorage.remove(OpenAccountParamKey);
          navigate(CustomerPagePath.SendSMS, {
            state: {
              phoneNumber: ret.data.Login.PhoneNumber,
              provisionalToken: ret.data.Login.ProvisionalToken,
            },
          });
        }
      },
    },
    [showToast, navigate],
  );

  const formik = useFormik<LoginParamType>({
    initialValues: {
      branch: '',
      account: '',
      password: '',
    },
    onSubmit,
    validate,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const onPasskeyLogin = useOnceSubmit<LoginParamType>(
    {
      onSubmit: async (values) => {
        const errors = validate(values);
        const accountErrors: Partial<Record<keyof LoginParamType, string>> = {};

        if (errors.branch) {
          accountErrors.branch = errors.branch;
        }
        if (errors.account) {
          accountErrors.account = errors.account;
        }

        if (Object.keys(accountErrors).length > 0) {
          formik.setErrors(accountErrors);
          return;
        }

        formik.setErrors({});
        const account = `${values.branch}-${values.account}`;
        const ret = await client.query(GetNegotiateTokenDocument, { account }, { requestPolicy: 'network-only' });

        const negotiateToken = ret.data?.GetNegotiateToken?.NegotiateToken;

        if (ret.data && ret.data.GetNegotiateToken?.Code !== 0) {
          showToast(ret.data.GetNegotiateToken?.Message ?? 'ログインに失敗しました', ToastMessageLevel.Error);
          return;
        }

        const url = process.env.REACT_APP_MFA_URL + '/common/sign_gate';
        const state = generateRandomNumericString(6);
        const codeVerifier = generatePkceCodeVerifier();
        const codeChallenge = await generatePkceCodeChallenge(codeVerifier);

        // 検証用にSessionストレージへ保存
        sessionStorage.setItem(SignGateStateKey, state);
        sessionStorage.setItem(SignGateCodeVerifierKey, codeVerifier);
        sessionStorage.setItem(SignGateAccountKey, account);

        const requestBody: SignGateRequestBody = {
          action: 'sign_in',
          auth_flow: 'passkey',
          state,
          code_challenge: codeChallenge,
          client_disp_name: 'FPG証券',
          user_disp_name: account,
          redirect_url: process.env.REACT_APP_SCHEME_FOR_MFA + '/mfaredirect',
          response_url_on_fail: process.env.REACT_APP_SCHEME_FOR_MFA + '/login',
        };

        const res = await fetchDataWithAuthorization<SignGateResponse>(url, negotiateToken, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (isSignGateSuccess(res)) {
          window.location.assign(res.redirect_url);
          return;
        }

        showToast(res.error_message ?? 'ログインに失敗しました', ToastMessageLevel.Error);
      },
    },
    [client, formik.setErrors, showToast],
  );

  return {
    isRevealPassword,
    togglePassword,
    formik,
    toastProps,
    onPasskeyLogin,
    fetchDataWithAuthorization,
  };
};
